import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
    ArrowLeft, Clock, User, DollarSign, CheckCircle, XCircle, 
    AlertTriangle, Loader2, RefreshCcw, Calculator, FileText
} from 'lucide-react';
import { cn } from '../lib/utils';

const PendingClosuresPage = () => {
    const navigate = useNavigate();
    const { api, user } = useAuth();
    
    const [loading, setLoading] = useState(true);
    const [pendingClosures, setPendingClosures] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    
    // Validation dialog
    const [validationDialog, setValidationDialog] = useState({ open: false, session: null, action: null });
    const [managerNotes, setManagerNotes] = useState('');
    const [validating, setValidating] = useState(false);
    
    // Check if user is authorized
    const role = user?.role;
    const isAuthorized = role && ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(role);
    
    // Fetch pending closures
    const fetchPendingClosures = useCallback(async () => {
        try {
            const res = await api().get('/pos/sessions/pending-closures');
            setPendingClosures(res.data.sessions || []);
        } catch (error) {
            console.error('Error fetching pending closures:', error);
            toast.error('Erreur lors du chargement des demandes');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [api]);
    
    useEffect(() => {
        if (isAuthorized) {
            fetchPendingClosures();
            // Auto-refresh every 30 seconds
            const interval = setInterval(fetchPendingClosures, 30000);
            return () => clearInterval(interval);
        }
    }, [fetchPendingClosures, isAuthorized]);
    
    // Handle validation/rejection
    const handleValidation = async (approved) => {
        if (!validationDialog.session) return;
        
        setValidating(true);
        try {
            await api().post(`/pos/sessions/${validationDialog.session.id}/validate-close`);
            
            toast.success(approved 
                ? `Fermeture de ${validationDialog.session.user_name} validée !` 
                : `Fermeture de ${validationDialog.session.user_name} rejetée`
            );
            
            setValidationDialog({ open: false, session: null, action: null });
            setManagerNotes('');
            fetchPendingClosures();
        } catch (error) {
            console.error('Error validating closure:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
        } finally {
            setValidating(false);
        }
    };
    
    // Handle rejection
    const handleRejection = async () => {
        if (!validationDialog.session) return;
        
        if (!managerNotes.trim()) {
            toast.error('Veuillez indiquer le motif du rejet');
            return;
        }
        
        setValidating(true);
        try {
            await api().post(`/pos/sessions/${validationDialog.session.id}/reject-close`, {
                reason: managerNotes
            });
            
            toast.success(`Fermeture de ${validationDialog.session.user_name} rejetée`);
            
            setValidationDialog({ open: false, session: null, action: null });
            setManagerNotes('');
            fetchPendingClosures();
        } catch (error) {
            console.error('Error rejecting closure:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors du rejet');
        } finally {
            setValidating(false);
        }
    };
    
    // Format time
    const formatTime = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    // Format date
    const formatDate = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'short'
        });
    };
    
    if (!isAuthorized) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted/30">
                <Card className="w-full max-w-md">
                    <CardHeader>
                        <CardTitle className="text-red-600">Accès refusé</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p>Vous n'avez pas les droits pour accéder à cette page.</p>
                        <Button onClick={() => navigate('/dashboard')} className="mt-4">
                            Retour au tableau de bord
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-muted/30 p-4 sm:p-6">
            {/* Header */}
            <div className="max-w-4xl mx-auto mb-6">
                <div className="flex items-center gap-4 mb-4">
                    <Button 
                        variant="outline" 
                        onClick={() => navigate(-1)}
                        className="gap-2"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Retour
                    </Button>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold">Demandes de fermeture</h1>
                        <p className="text-muted-foreground">
                            Sessions en attente de validation
                        </p>
                    </div>
                    <Button 
                        variant="outline" 
                        onClick={() => {
                            setRefreshing(true);
                            fetchPendingClosures();
                        }}
                        disabled={refreshing}
                    >
                        <RefreshCcw className={cn("w-4 h-4", refreshing && "animate-spin")} />
                    </Button>
                </div>
                
                {/* Stats */}
                <div className="flex items-center gap-4">
                    <Badge variant={pendingClosures.length > 0 ? "destructive" : "secondary"} className="text-lg px-4 py-2">
                        {pendingClosures.length} en attente
                    </Badge>
                </div>
            </div>
            
            {/* Content */}
            <div className="max-w-4xl mx-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                ) : pendingClosures.length === 0 ? (
                    <Card>
                        <CardContent className="flex flex-col items-center justify-center py-20">
                            <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                            <h3 className="text-xl font-semibold text-green-700">Aucune demande en attente</h3>
                            <p className="text-muted-foreground mt-2">
                                Toutes les demandes de fermeture ont été traitées
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {pendingClosures.map((session) => {
                            const hasDifference = Math.abs(session.difference || 0) > 0;
                            
                            return (
                                <Card 
                                    key={session.id} 
                                    className={cn(
                                        "border-2 transition-all",
                                        hasDifference 
                                            ? "border-amber-300 bg-amber-50/50" 
                                            : "border-green-300 bg-green-50/50"
                                    )}
                                >
                                    <CardHeader className="pb-2">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <CardTitle className="text-lg flex items-center gap-2">
                                                    <User className="w-5 h-5" />
                                                    {session.user_name}
                                                </CardTitle>
                                                <CardDescription>
                                                    {session.register_name || 'Caisse'} • {session.register_sector || 'Restaurant'}
                                                </CardDescription>
                                            </div>
                                            <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                                                <Clock className="w-3 h-3 mr-1" />
                                                En attente
                                            </Badge>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                                            <div>
                                                <p className="text-xs text-muted-foreground">Ouverture</p>
                                                <p className="font-semibold">
                                                    {formatTime(session.opened_at)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Demande</p>
                                                <p className="font-semibold">
                                                    {formatTime(session.closing_requested_at)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Fond de caisse</p>
                                                <p className="font-semibold">
                                                    {(session.opening_cash || 0).toLocaleString()} F
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Ventes espèces</p>
                                                <p className="font-semibold text-green-600">
                                                    +{(session.cash_sales || 0).toLocaleString()} F
                                                </p>
                                            </div>
                                        </div>
                                        
                                        {/* Cash comparison */}
                                        <div className={cn(
                                            "p-4 rounded-lg border-2 mb-4",
                                            hasDifference 
                                                ? "bg-amber-100 border-amber-300" 
                                                : "bg-green-100 border-green-300"
                                        )}>
                                            <div className="grid grid-cols-3 gap-4 text-center">
                                                <div>
                                                    <p className="text-xs text-muted-foreground mb-1">Attendu</p>
                                                    <p className="text-xl font-bold">
                                                        {(session.expected_cash || 0).toLocaleString()} F
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-muted-foreground mb-1">Compté</p>
                                                    <p className="text-xl font-bold">
                                                        {(session.counted_cash || 0).toLocaleString()} F
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-muted-foreground mb-1">Écart</p>
                                                    <p className={cn(
                                                        "text-xl font-bold",
                                                        session.difference > 0 ? "text-green-600" :
                                                        session.difference < 0 ? "text-red-600" :
                                                        "text-green-600"
                                                    )}>
                                                        {session.difference > 0 ? '+' : ''}{(session.difference || 0).toLocaleString()} F
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Notes */}
                                        {session.closing_notes && (
                                            <div className="p-3 bg-muted rounded-lg mb-4">
                                                <p className="text-xs text-muted-foreground mb-1">
                                                    <FileText className="w-3 h-3 inline mr-1" />
                                                    Notes de l'employé
                                                </p>
                                                <p className="text-sm">{session.closing_notes}</p>
                                            </div>
                                        )}
                                        
                                        {/* Actions */}
                                        <div className="flex gap-3">
                                            <Button 
                                                className="flex-1 bg-green-600 hover:bg-green-700"
                                                onClick={() => setValidationDialog({ 
                                                    open: true, 
                                                    session, 
                                                    action: 'approve' 
                                                })}
                                                data-testid={`approve-btn-${session.id}`}
                                            >
                                                <CheckCircle className="w-4 h-4 mr-2" />
                                                Valider
                                            </Button>
                                            <Button 
                                                variant="destructive"
                                                className="flex-1"
                                                onClick={() => setValidationDialog({ 
                                                    open: true, 
                                                    session, 
                                                    action: 'reject' 
                                                })}
                                                data-testid={`reject-btn-${session.id}`}
                                            >
                                                <XCircle className="w-4 h-4 mr-2" />
                                                Rejeter
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>
            
            {/* Validation Dialog */}
            <Dialog 
                open={validationDialog.open} 
                onOpenChange={(open) => {
                    if (!open) {
                        setValidationDialog({ open: false, session: null, action: null });
                        setManagerNotes('');
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {validationDialog.action === 'approve' ? (
                                <>
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Valider la fermeture
                                </>
                            ) : (
                                <>
                                    <XCircle className="w-5 h-5 text-red-600" />
                                    Rejeter la fermeture
                                </>
                            )}
                        </DialogTitle>
                        <DialogDescription>
                            {validationDialog.action === 'approve' 
                                ? `Confirmez la validation de la fermeture de caisse de ${validationDialog.session?.user_name}`
                                : `Indiquez le motif du rejet pour ${validationDialog.session?.user_name}`
                            }
                        </DialogDescription>
                    </DialogHeader>
                    
                    {validationDialog.session && (
                        <div className="py-4">
                            {/* Summary */}
                            <div className="p-4 bg-muted rounded-lg mb-4">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">Caisse:</span>
                                        <span className="ml-2 font-semibold">{validationDialog.session.register_name}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Écart:</span>
                                        <span className={cn(
                                            "ml-2 font-semibold",
                                            validationDialog.session.difference > 0 ? "text-green-600" :
                                            validationDialog.session.difference < 0 ? "text-red-600" :
                                            "text-green-600"
                                        )}>
                                            {validationDialog.session.difference > 0 ? '+' : ''}
                                            {(validationDialog.session.difference || 0).toLocaleString()} F
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Notes input for rejection */}
                            {validationDialog.action === 'reject' && (
                                <div>
                                    <label className="text-sm font-medium mb-2 block">
                                        Motif du rejet *
                                    </label>
                                    <Textarea
                                        placeholder="Ex: Écart trop important, veuillez recompter..."
                                        value={managerNotes}
                                        onChange={(e) => setManagerNotes(e.target.value)}
                                        rows={3}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    
                    <DialogFooter className="gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setValidationDialog({ open: false, session: null, action: null });
                                setManagerNotes('');
                            }}
                        >
                            Annuler
                        </Button>
                        {validationDialog.action === 'approve' ? (
                            <Button 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleValidation(true)}
                                disabled={validating}
                            >
                                {validating ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                )}
                                Confirmer la validation
                            </Button>
                        ) : (
                            <Button 
                                variant="destructive"
                                onClick={handleRejection}
                                disabled={validating || !managerNotes.trim()}
                            >
                                {validating ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <XCircle className="w-4 h-4 mr-2" />
                                )}
                                Confirmer le rejet
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PendingClosuresPage;
